import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Save } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FieldLabel } from '../FieldLabel'

interface SaveQueryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Name to start from — the loaded query's, so saving again updates it. */
  defaultName: string
  /** Names already saved, to warn before one is overwritten. */
  existingNames: string[]
  saving: boolean
  onSave: (name: string) => void
}

/** Names a quick report so it can be loaded again later. */
export function SaveQueryDialog({
  open,
  onOpenChange,
  defaultName,
  existingNames,
  saving,
  onSave,
}: SaveQueryDialogProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(defaultName)

  useEffect(() => {
    if (open) setName(defaultName)
  }, [open, defaultName])

  const trimmed = name.trim()
  const overwrites = existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (trimmed) onSave(trimmed)
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('queries.saved.saveTitle')}</DialogTitle>
            <DialogDescription>{t('queries.saved.saveHelp')}</DialogDescription>
          </DialogHeader>

          <div className="p-field">
            <FieldLabel htmlFor="query-name" hint={overwrites ? t('queries.saved.overwrites') : undefined}>
              {t('queries.saved.name')}
            </FieldLabel>
            <Input id="query-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!trimmed || saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              {overwrites ? t('queries.saved.update') : t('queries.saved.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
